<!--
  Barcode Analysis — the XYLO PRD scans, two views of the same data.

    1. XYLO PRD  : poles scanned on the field with the PWA (pole_entries table).
                   Cross-table: one row per period (day or month), one column per
                   operator, total volume in the cells. Aggregated SERVER-SIDE
                   (/api/traceability/analysis) — the raw /poles endpoint is
                   capped at 2000 rows and would silently truncate.
    2. Barcodes  : same pole_entries rows, NOT aggregated — one line per scanned
                   label (label_code), shown by day, by month or as a flat list
                   by barcode (/api/traceability/barcodes). The date range above
                   the cards drives BOTH tables so their totals stay comparable.
                   Pieces under the tagging threshold (shorter than 2 m or
                   Ø ≤ 15 cm) may be saved WITHOUT a label,
                   several identical pieces per line (`quantity`): such a line
                   shows "no tag × N" and counts N pieces; volume_m3 is already
                   the line total.

  Reached from Production → list ("Barcode analysis" button). The conveyor camera
  (XYLO VISION · EW1) has its own page: CameraAnalysis.vue ("Camera analysis").
-->
<template>
  <div id="production-analysis">
    <div class="vx-row">
      <div class="vx-col w-full">

        <!-- Date range shared by the two XYLO PRD tables below -->
        <div class="flex flex-wrap items-end justify-end mb-4">
          <div class="mr-3">
            <label class="filter-label">From</label>
            <input type="date" v-model="from" class="date-input" @change="load" />
          </div>
          <div class="mr-3">
            <label class="filter-label">To</label>
            <input type="date" v-model="to" class="date-input" @change="load" />
          </div>
          <vs-button
            class="mr-2"
            type="border"
            color="primary"
            size="small"
            @click="clearRange"
            :disabled="!from && !to"
          >
            All time
          </vs-button>
          <vs-button
            type="border"
            color="primary"
            size="small"
            icon-pack="feather"
            icon="icon-refresh-cw"
            @click="load"
          >
            {{ loading || bcLoading ? 'Loading…' : 'Refresh' }}
          </vs-button>
        </div>

        <!-- ══════════════ XYLO PRD ══════════════ -->
        <vx-card class="mb-base">
          <div class="flex flex-wrap items-start justify-between mb-6">
            <div>
              <h3 class="mb-1">XYLO PRD — Poles</h3>
              <p class="src-note mb-0">
                Volume produced per operator, from the labels scanned with the XYLO PRD app.
              </p>
            </div>
          </div>

          <!-- Day / Month: same table, two grains -->
          <div class="flex flex-wrap items-center mb-4">
            <vs-button
              v-for="g in groups"
              :key="g.key"
              class="mr-2"
              size="small"
              :type="group === g.key ? 'filled' : 'border'"
              color="primary"
              @click="setGroup(g.key)"
            >
              {{ g.label }}
            </vs-button>
          </div>

          <div v-if="loading" class="text-center py-12 text-muted">Loading production data…</div>

          <div v-else-if="!periods.length" class="empty-state">
            <feather-icon icon="InboxIcon" svgClasses="w-6 h-6" class="mb-2" />
            <div class="font-medium">No pole recorded for this period</div>
            <div class="text-sm">Entries appear here as soon as an operator scans a label in XYLO PRD.</div>
          </div>

          <!-- Rows only: one block per period, one line per person inside it.
               People are in ROWS, never in columns — there will be dozens of
               them and a column each would be unreadable. -->
          <div v-else class="table-scroll">
            <table class="pivot">
              <thead>
                <tr>
                  <th class="col-label">{{ group === 'month' ? 'Month' : 'Date' }} / Operator</th>
                  <th class="num col-total">Volume</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in displayRows" :key="row.key" :class="row.type === 'period' ? 'period-row' : ''">
                  <td :class="row.type === 'period' ? 'col-label' : 'col-label op-label'">{{ row.label }}</td>
                  <td class="num col-total">{{ formatM3(row.volume) }}</td>
                </tr>
              </tbody>
              <tfoot>
                <tr>
                  <td class="col-label">TOTAL</td>
                  <td class="num col-total">{{ formatM3(grandTotal) }}</td>
                </tr>
              </tfoot>
            </table>
          </div>

          <p v-if="periods.length" class="src-note mt-4">
            Volume is the cylinder volume recomputed server-side at save time:
            <b>V = π × (Ø/2)² × L</b>. Days and months are Europe/Bucharest.
          </p>
        </vx-card>

        <!-- ══════════════ BARCODES ══════════════ -->
        <vx-card class="mb-base">
          <div class="flex flex-wrap items-start justify-between mb-6">
            <div>
              <h3 class="mb-1">XYLO PRD — Barcodes</h3>
              <p class="src-note mb-0">
                Every label scanned with the XYLO PRD app — one line per barcode, with who
                scanned it, when, and what was measured.
              </p>
            </div>

            <div class="flex flex-wrap items-end">
              <div class="mr-3">
                <label class="filter-label">Search barcode / operator</label>
                <input type="text" v-model.trim="bcSearch" class="date-input" placeholder="e.g. 260002" />
              </div>
              <vs-button
                type="border"
                color="primary"
                size="small"
                icon-pack="feather"
                icon="icon-download"
                :disabled="!bcFiltered.length"
                @click="exportBarcodesCsv"
              >
                Export CSV
              </vs-button>
            </div>
          </div>

          <div class="flex flex-wrap items-center mb-4">
            <vs-button
              v-for="v in bcViews"
              :key="v.key"
              class="mr-2"
              size="small"
              :type="bcView === v.key ? 'filled' : 'border'"
              color="primary"
              @click="setBcView(v.key)"
            >
              {{ v.label }}
            </vs-button>
          </div>

          <div v-if="bcTruncated" class="warn-note mb-4">
            Only the {{ bcCap }} most recent barcodes are listed — narrow the date range to see older ones.
          </div>

          <div v-if="bcLoading" class="text-center py-12 text-muted">Loading barcodes…</div>

          <div v-else-if="!bcFiltered.length" class="empty-state">
            <feather-icon icon="InboxIcon" svgClasses="w-6 h-6" class="mb-2" />
            <div class="font-medium">{{ bcRows.length ? 'No barcode matches this search' : 'No barcode scanned for this period' }}</div>
            <div v-if="!bcRows.length" class="text-sm">Barcodes appear here as soon as an operator scans a label in XYLO PRD.</div>
          </div>

          <div v-else class="table-scroll">
            <table class="pivot">
              <thead>
                <tr>
                  <th class="col-label">Barcode</th>
                  <th v-if="bcShowDate" class="col-label">Date</th>
                  <th class="col-label">Time</th>
                  <th class="col-label">Operator</th>
                  <th class="col-label">Category</th>
                  <th class="num">Ø cm</th>
                  <th class="num">L m</th>
                  <th class="num col-total">Volume</th>
                </tr>
              </thead>

              <!-- By day / by month: one collapsible block per period. Only the
                   open blocks are rendered — a month can hold thousands of labels. -->
              <template v-if="bcView !== 'barcode'">
                <tbody v-for="g in bcGroups" :key="g.key">
                  <tr class="period-row clickable" @click="toggleBcGroup(g.key)">
                    <td class="col-label" :colspan="bcColCount - 1">
                      <feather-icon
                        :icon="bcOpen[g.key] ? 'ChevronDownIcon' : 'ChevronRightIcon'"
                        svgClasses="w-4 h-4"
                        class="align-middle mr-1"
                      />
                      {{ g.label }}
                      <span class="count-pill">{{ g.pieces }} {{ g.pieces === 1 ? 'piece' : 'pieces' }}</span>
                    </td>
                    <td class="num col-total">{{ formatM3(g.volume) }}</td>
                  </tr>
                  <template v-if="bcOpen[g.key]">
                    <tr v-for="r in g.rows" :key="r.id">
                      <td class="col-label op-label barcode">{{ barcodeLabel(r) }}</td>
                      <td v-if="bcShowDate" class="col-label">{{ dayLabel(r.day) }}</td>
                      <td class="col-label">{{ r.time }}</td>
                      <td class="col-label">{{ r.employee_name }}</td>
                      <td class="col-label">{{ r.category_name || r.essence || '—' }}</td>
                      <td class="num">{{ formatDim(r.diametre_cm) }}</td>
                      <td class="num">{{ formatDim(r.longueur_m) }}</td>
                      <td class="num col-total">{{ formatM3(r.volume_m3) }}</td>
                    </tr>
                  </template>
                </tbody>
              </template>

              <!-- By barcode: flat list in barcode order -->
              <tbody v-else>
                <tr v-for="r in bcFlatPage" :key="r.id">
                  <td class="col-label barcode">{{ barcodeLabel(r) }}</td>
                  <td class="col-label">{{ dayLabel(r.day) }}</td>
                  <td class="col-label">{{ r.time }}</td>
                  <td class="col-label">{{ r.employee_name }}</td>
                  <td class="col-label">{{ r.category_name || r.essence || '—' }}</td>
                  <td class="num">{{ formatDim(r.diametre_cm) }}</td>
                  <td class="num">{{ formatDim(r.longueur_m) }}</td>
                  <td class="num col-total">{{ formatM3(r.volume_m3) }}</td>
                </tr>
              </tbody>

              <tfoot>
                <tr>
                  <td class="col-label" :colspan="bcColCount - 1">
                    TOTAL
                    <span class="count-pill">{{ bcPieces }} {{ bcPieces === 1 ? 'piece' : 'pieces' }}</span>
                  </td>
                  <td class="num col-total">{{ formatM3(bcTotal) }}</td>
                </tr>
              </tfoot>
            </table>

            <div v-if="bcView === 'barcode' && bcFlatPage.length < bcFlat.length" class="text-center mt-4">
              <vs-button type="border" color="primary" size="small" @click="bcLimit += bcPageSize">
                Show more ({{ bcFlat.length - bcFlatPage.length }} remaining)
              </vs-button>
            </div>
          </div>
        </vx-card>

      </div>
    </div>
  </div>
</template>

<script>
import axios from '@/axios.js'

export default {
  name: 'ProductionAnalysis',
  data () {
    return {
      loading: false,
      group: 'day',
      from: '',
      to: '',
      // One row per (period, operator) — the cross-table is built below.
      rows: [],
      groups: [
        { key: 'day',   label: 'By day' },
        { key: 'month', label: 'By month' }
      ],

      // ── Barcode report: raw rows (one per label), regrouped on screen ──
      bcLoading: false,
      bcRows: [],
      bcTruncated: false,
      bcCap: 0,
      bcView: 'day',
      bcSearch: '',
      bcOpen: {},          // period key → expanded?
      bcPageSize: 300,
      bcLimit: 300,        // flat "by barcode" list is paged, never dumped whole
      bcViews: [
        { key: 'day',     label: 'By day' },
        { key: 'month',   label: 'By month' },
        { key: 'barcode', label: 'By barcode' }
      ]
    }
  },
  computed: {
    // Periods, most recent first (the API already sorts them that way).
    periods () {
      const seen = []
      this.rows.forEach((r) => { if (!seen.includes(r.period)) seen.push(r.period) })
      return seen
    },
    // Flat list of table lines: a period line carrying the period's total,
    // followed by one indented line per person (biggest producer first).
    displayRows () {
      const out = []
      this.periods.forEach((p) => {
        const people = this.rows
          .filter((r) => r.period === p)
          .sort((a, b) => (Number(b.volume_m3) || 0) - (Number(a.volume_m3) || 0))
        out.push({
          key: `p-${p}`,
          type: 'period',
          label: this.periodLabel(p),
          volume: people.reduce((s, r) => s + (Number(r.volume_m3) || 0), 0)
        })
        people.forEach((r) => {
          out.push({
            key: `e-${p}-${r.employee_id}`,
            type: 'operator',
            label: r.employee_name || `person#${r.employee_id}`,
            volume: Number(r.volume_m3) || 0
          })
        })
      })
      return out
    },
    grandTotal () {
      return this.rows.reduce((s, r) => s + (Number(r.volume_m3) || 0), 0)
    },

    // ── Barcodes ──
    bcShowDate () { return this.bcView !== 'day' },
    bcColCount () { return this.bcShowDate ? 8 : 7 },
    bcFiltered () {
      const q = this.bcSearch.toLowerCase()
      if (!q) return this.bcRows
      const hit = (v) => String(v || '').toLowerCase().includes(q)
      return this.bcRows.filter((r) => hit(r.label_code) || hit(r.employee_name))
    },
    // Periods most recent first, labels most recent first inside (API order).
    bcGroups () {
      const byKey = {}
      const out = []
      this.bcFiltered.forEach((r) => {
        const key = this.bcView === 'month' ? r.day.slice(0, 7) : r.day
        if (!byKey[key]) {
          byKey[key] = { key, label: this.periodLabel(key, this.bcView), rows: [], volume: 0, pieces: 0 }
          out.push(byKey[key])
        }
        byKey[key].rows.push(r)
        byKey[key].volume += Number(r.volume_m3) || 0
        byKey[key].pieces += this.pieces(r)
      })
      return out
    },
    // Barcode order; codes are digits, so compare numerically ('9' before '10').
    // Entries saved without a label go last.
    bcFlat () {
      return this.bcFiltered.slice().sort((a, b) => {
        if (!a.label_code || !b.label_code) return (a.label_code ? 0 : 1) - (b.label_code ? 0 : 1)
        return String(a.label_code).localeCompare(String(b.label_code), undefined, { numeric: true })
      })
    },
    bcFlatPage () { return this.bcFlat.slice(0, this.bcLimit) },
    bcTotal () {
      return this.bcFiltered.reduce((s, r) => s + (Number(r.volume_m3) || 0), 0)
    },
    // Pieces, not lines: an untagged lot (under the tagging threshold) is one line for N poles.
    bcPieces () {
      return this.bcFiltered.reduce((s, r) => s + this.pieces(r), 0)
    }
  },
  watch: {
    bcSearch () {
      this.bcLimit = this.bcPageSize
      // A search is a lookup: show the hits without making the user unfold periods.
      if (this.bcSearch) this.openAllBcGroups()
      else this.resetBcOpen()
    }
  },
  created () {
    this.load()
  },
  methods: {
    setGroup (g) {
      if (this.group === g) return
      this.group = g
      this.loadVolumes()
    },
    setBcView (v) {
      if (this.bcView === v) return
      this.bcView = v
      this.bcLimit = this.bcPageSize
      this.resetBcOpen()
    },
    toggleBcGroup (key) {
      this.$set(this.bcOpen, key, !this.bcOpen[key])
    },
    // Most recent period unfolded, the rest folded.
    resetBcOpen () {
      this.bcOpen = {}
      if (this.bcSearch) return this.openAllBcGroups()
      if (this.bcGroups.length) this.$set(this.bcOpen, this.bcGroups[0].key, true)
    },
    openAllBcGroups () {
      const open = {}
      this.bcGroups.forEach((g) => { open[g.key] = true })
      this.bcOpen = open
    },
    clearRange () {
      this.from = ''
      this.to = ''
      this.load()
    },
    // Both tables follow the shared date range.
    load () {
      this.loadVolumes()
      this.loadBarcodes()
    },
    rangeParams () {
      const params = {}
      if (this.from) params.from = this.from
      if (this.to) params.to = this.to
      return params
    },
    loadBarcodes () {
      this.bcLoading = true
      axios.get('/traceability/barcodes', { params: this.rangeParams() })
        .then((res) => {
          const d = res.data || {}
          this.bcRows = d.rows || []
          this.bcTruncated = !!d.truncated
          this.bcCap = d.cap || 0
        })
        .catch((err) => {
          console.error('Error loading barcode report:', err)
          this.$vs.notify({ color: 'danger', title: 'Error', text: 'Could not load the XYLO PRD barcodes.' })
          this.bcRows = []
          this.bcTruncated = false
        })
        .finally(() => {
          this.bcLoading = false
          this.bcLimit = this.bcPageSize
          this.resetBcOpen()
        })
    },
    pieces (r) {
      const q = parseInt(r.quantity, 10)
      return q > 0 ? q : 1
    },
    // Tagged pole → its label; untagged lot (< 2 m or Ø ≤ 15 cm) → "no tag × N".
    barcodeLabel (r) {
      if (r.label_code) return r.label_code
      const q = this.pieces(r)
      return q > 1 ? `no tag × ${q}` : 'no tag'
    },
    // Exports what is on screen: current search, current view's order.
    exportBarcodesCsv () {
      const rows = this.bcView === 'barcode' ? this.bcFlat : this.bcFiltered
      const cell = (v) => `"${String(v === null || v === undefined ? '' : v).replace(/"/g, '""')}"`
      const head = ['barcode', 'date', 'time', 'operator', 'category', 'essence', 'diameter_cm', 'length_m', 'pieces', 'volume_m3', 'uid']
      const lines = rows.map((r) => [
        r.label_code, r.day, r.time, r.employee_name, r.category_name, r.essence,
        r.diametre_cm, r.longueur_m, this.pieces(r), r.volume_m3, r.uid
      ].map(cell).join(','))
      // BOM so Excel reads the UTF-8 operator names
      const blob = new Blob([`\ufeff${head.join(',')}\n${lines.join('\n')}`], { type: 'text/csv;charset=utf-8' })
      const a = document.createElement('a')
      a.href = URL.createObjectURL(blob)
      a.download = `barcodes_${this.from || 'start'}_${this.to || new Date().toISOString().slice(0, 10)}.csv`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(a.href)
    },
    loadVolumes () {
      this.loading = true
      const params = Object.assign({ group: this.group }, this.rangeParams())
      axios.get('/traceability/analysis', { params })
        .then((res) => { this.rows = (res.data && res.data.rows) || [] })
        .catch((err) => {
          console.error('Error loading production analysis:', err)
          this.$vs.notify({ color: 'danger', title: 'Error', text: 'Could not load XYLO PRD production data.' })
          this.rows = []
        })
        .finally(() => { this.loading = false })
    },
    // '2026-08-04' → '04/08/2026' ; '2026-08' → 'August 2026'
    periodLabel (period, group) {
      if ((group || this.group) === 'month') {
        const [y, m] = period.split('-')
        const d = new Date(Number(y), Number(m) - 1, 1)
        return isNaN(d) ? period : d.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })
      }
      const parts = period.split('-')
      return parts.length === 3 ? `${parts[2]}/${parts[1]}/${parts[0]}` : period
    },
    dayLabel (day) { return this.periodLabel(day, 'day') },
    formatDim (n) {
      if (n === null || n === undefined || isNaN(n)) return '—'
      return new Intl.NumberFormat('en-GB', { maximumFractionDigits: 2 }).format(Number(n))
    },
    formatM3 (n) {
      if (n === null || n === undefined || isNaN(n)) return '—'
      return `${new Intl.NumberFormat('en-GB', {
        minimumFractionDigits: 3,
        maximumFractionDigits: 3
      }).format(Number(n))} m³`
    }
  }
}
</script>

<style scoped>
.text-muted { color: #6c757d; }

.src-note { font-size: 0.78rem; color: #94a3b8; max-width: 640px; }
.filter-label { display: block; font-size: 0.7rem; color: #94a3b8; margin-bottom: 3px; }
.date-input {
  border: 1px solid #dae1e7; border-radius: 5px; padding: 6px 9px;
  font-size: 0.85rem; color: #1e293b; background: #fff;
}

/* Plain HTML table — grouped rows (period line + one line per person) are not
   something vs-table's flat slot rendering expresses. */
.table-scroll { overflow-x: auto; }
.pivot { width: 100%; border-collapse: collapse; font-size: 0.86rem; }
.pivot th, .pivot td { padding: 9px 14px; border-bottom: 1px solid #eef2f7; white-space: nowrap; }
.pivot thead th {
  font-size: 0.72rem; font-weight: 700; text-transform: uppercase; letter-spacing: .04em;
  color: #64748b; background: #f8fafc; border-bottom: 1px solid #e2e8f0;
}
.pivot tbody tr:hover td { background: #f8fafc; }
.pivot .num { text-align: right; }
.pivot .col-label { text-align: left; color: #1e293b; }
.pivot .col-total { font-weight: 600; color: #1e293b; width: 180px; }
/* period line = the day/month total, people indented under it */
.pivot .period-row td {
  background: #f8fafc; font-weight: 700; border-top: 1px solid #e2e8f0;
}
.pivot .period-row .col-total { color: #7367f0; }
.pivot .op-label { padding-left: 34px; color: #475569; }
.pivot tfoot td {
  border-top: 2px solid #cbd5e1; background: #f1f5f9; font-weight: 700; color: #1e293b;
}

.pivot .clickable { cursor: pointer; user-select: none; }
.pivot .barcode { font-family: SFMono-Regular, Menlo, Consolas, monospace; font-weight: 600; letter-spacing: .04em; }
.count-pill {
  display: inline-block; margin-left: 10px; padding: 1px 9px; border-radius: 999px;
  background: #eef2ff; color: #4f46e5; font-size: 0.7rem; font-weight: 600;
}
.warn-note {
  background: #fff7ed; color: #9a3412; border: 1px solid #fed7aa;
  border-radius: 6px; padding: 8px 12px; font-size: 0.8rem;
}

.empty-state { text-align: center; color: #94a3b8; padding: 40px 10px; }
</style>
